import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import DatasetDefinitionsContext from '../../contexts/DatasetDefinitionsContext';
import Loading from '../../components/Loading/Loading';
import BonusPeriodsContext from '../../contexts/BonusPeriodsContext';
import PeriodsContext from '../../contexts/PeriodsContext';
import BonusSubAppBoardSignOffsProvider from './BonusSubAppBoardSignOffsProvider';
import getDefaultBonusPeriod from './getDefaultBonusPeriod';
import getDefaultSubPeriod from './getDefaultSubPeriod';
import programHasSubPeriods from './programHasSubPeriods';
import resolveDefaultBonusPeriod from './resolveDefaultBonusPeriod';
import { useLocation, useSearchParams } from 'react-router-dom';
import appNavItemTypeCheckers from '../../types/appNavItemTypeCheckers';

export const BonusAppContext = React.createContext<
  | {
      bonusApp: V3.BonusApp;
      dataset: FleetOps.DatasetDefinition;
      bonusProgram: BonusTypes.BonusProgram;
      selectedBonusPeriod: BonusTypes.BonusPeriod;
      demoOverridenStatusFields: { [elasticDocumentId: string]: boolean };
      setDemoOverridenStatusFields: React.Dispatch<
        React.SetStateAction<{ [elasticDocumentId: string]: boolean }>
      >;
    }
  | undefined
>(undefined);

const useBonusPeriods = ({
  dataset,
  programId,
  isApprovalsScreen,
}: {
  dataset: string | undefined;
  programId?: string;
  // On the Approvals screen the default period keeps the status-priority logic
  // (getDefaultBonusPeriod → the period awaiting sign-off). Everywhere else, a
  // sub-period programme defaults to the period containing today so the board
  // opens on the current week rather than the sign-off period's last week.
  isApprovalsScreen?: boolean;
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { bonusPrograms, bonusPeriodsLookup } = useContext(
    DatasetDefinitionsContext,
  );

  const program = useMemo(() => {
    if (programId) {
      return bonusPrograms.find((p) => p.id === programId);
    }
    if (!dataset) {
      return;
    }
    return bonusPrograms.find((p) => p.dataset === dataset);
  }, [bonusPrograms, dataset, programId]);

  const bonusPeriods = useMemo(() => {
    if (!program) {
      return undefined;
    }
    const periods = bonusPeriodsLookup[program.id];
    if (periods) {
      return periods;
    }
    return undefined;
  }, [bonusPeriodsLookup, program]);

  // Sub-period setups (weekly calculation on a multi-week sign-off cycle) are
  // URL-param-free: selection is driven purely by per-screen default logic
  // rather than ?s=/?sp=, which kept drifting out of sync. Ordinary programmes
  // keep the ?s= deep-link/persistence behaviour unchanged.
  const usesSubPeriods = useMemo(
    () => programHasSubPeriods(program),
    [program],
  );

  // Tracks the previous screen so the param-free path can re-apply the default
  // when the user crosses between a content board and the Approvals screen.
  const prevIsApprovalsScreenRef = useRef(isApprovalsScreen);

  const [selectedBonusPeriodId, setSelectedBonusPeriodId] = useState<
    string | undefined
  >(undefined);

  const onBonusPeriodSelected = useCallback(
    (bonusPeriod: BonusTypes.BonusPeriod | BonusPeriod | undefined) => {
      setSelectedBonusPeriodId(bonusPeriod ? bonusPeriod.id : undefined);
      // Sub-period setups don't persist selection in the URL (see usesSubPeriods).
      if (usesSubPeriods) {
        return;
      }
      setSearchParams((c) => {
        if (bonusPeriod) {
          c.set('s', bonusPeriod.startDate);
        } else {
          c.delete('s');
        }
        return c;
      });
    },
    [setSearchParams, usesSubPeriods],
  );

  const setSelectedPeriod = useCallback(
    (period: Period | undefined) => {
      if (!bonusPeriods || !period) {
        setSelectedBonusPeriodId(undefined);
        return;
      }

      const bonusPeriod = bonusPeriods.find(
        (p) => p.startDate === period.startDate,
      );
      setSelectedBonusPeriodId(bonusPeriod ? bonusPeriod.id : undefined);
    },
    [bonusPeriods],
  );

  // Bonus periods load in a second Firestore wave that is not gated by the
  // splash screen, so they can arrive after this hook mounts. Re-resolving on
  // late-arriving periods (rather than seeding once at mount) is what stops the
  // period picker staying hidden on a fresh navigation, mirroring the non-bonus
  // BoardPeriodsProvider. The two branches below differ only in whether the URL
  // participates: ordinary programmes persist selection in ?s=; sub-period
  // setups are param-free and defaulted per screen.
  useEffect(() => {
    if (!bonusPeriods) {
      return;
    }

    // A selection is only kept while it belongs to this program's periods. The
    // provider can outlive a change of program (e.g. switching between entity
    // view board tabs on different bonus datasets), leaving the previous
    // program's period id selected, which would resolve to no period at all.
    const isProgramPeriod = (id: string | undefined): id is string =>
      !!id && bonusPeriods.some((bp) => bp.id === id);

    // Sub-period setups: no URL params. Drive from the per-screen default and
    // re-apply it whenever the screen changes (Approvals ↔ content board), so
    // Approvals opens on the sign-off period and a board opens on the current
    // week. In-session picks are kept until the screen changes.
    if (usesSubPeriods) {
      const screenChanged =
        prevIsApprovalsScreenRef.current !== isApprovalsScreen;
      prevIsApprovalsScreenRef.current = isApprovalsScreen;
      setSelectedBonusPeriodId((current) => {
        const programCurrent = isProgramPeriod(current) ? current : undefined;
        if (programCurrent && !screenChanged) {
          return programCurrent;
        }
        const defaultBonusPeriod = resolveDefaultBonusPeriod({
          bonusPeriods,
          isApprovalsScreen: !!isApprovalsScreen,
        });
        return defaultBonusPeriod ? defaultBonusPeriod.id : programCurrent;
      });
      return;
    }

    // Ordinary programmes: the ?s= URL param wins (deep-link / cross-navigation
    // persistence), otherwise fall back to the status-priority default period.
    const startDate = searchParams.get('s');
    if (startDate) {
      const bonusPeriodFromUrl = bonusPeriods.find(
        (bp) => bp.startDate === startDate,
      );
      if (bonusPeriodFromUrl) {
        setSelectedBonusPeriodId(bonusPeriodFromUrl.id);
        return;
      }
    }

    setSelectedBonusPeriodId((current) => {
      if (isProgramPeriod(current)) {
        return current;
      }
      const defaultBonusPeriod = getDefaultBonusPeriod(bonusPeriods);
      return defaultBonusPeriod ? defaultBonusPeriod.id : undefined;
    });
  }, [bonusPeriods, searchParams, isApprovalsScreen, usesSubPeriods]);

  const selectedBonusPeriod = useMemo(():
    | BonusTypes.BonusPeriod
    | undefined => {
    if (!selectedBonusPeriodId || !bonusPeriods) {
      return undefined;
    }
    return bonusPeriods.find((bp) => bp.id === selectedBonusPeriodId);
  }, [bonusPeriods, selectedBonusPeriodId]);

  // --- Sub-period selection (programs with the sub-period pattern only) ---
  // Tracked by startDate (sub-periods carry no id), mirroring the root ?s= sync.
  const [selectedSubPeriodStart, setSelectedSubPeriodStart] = useState<
    string | undefined
  >(undefined);

  const onSubPeriodSelected = useCallback((subPeriod: Period | undefined) => {
    setSelectedSubPeriodStart(subPeriod ? subPeriod.startDate : undefined);
  }, []);

  // Resolve the selected sub-period whenever the root period changes or periods
  // arrive: keep the in-session week while it still belongs to the selected
  // period, otherwise fall back to the default window (the most recently
  // completed week, see getDefaultSubPeriod). No URL param — the week is
  // re-derived from the root period. Clears for programs without sub-periods so
  // they stay inert.
  useEffect(() => {
    const subPeriods = selectedBonusPeriod?.subPeriods;
    if (!subPeriods || subPeriods.length === 0) {
      setSelectedSubPeriodStart(undefined);
      return;
    }

    setSelectedSubPeriodStart((current) => {
      if (current && subPeriods.some((sp) => sp.startDate === current)) {
        return current;
      }
      const defaultSubPeriod = getDefaultSubPeriod(subPeriods);
      return defaultSubPeriod ? defaultSubPeriod.startDate : undefined;
    });
  }, [selectedBonusPeriod]);

  const selectedSubPeriod = useMemo((): Period | undefined => {
    const subPeriods = selectedBonusPeriod?.subPeriods;
    if (!subPeriods || !selectedSubPeriodStart) {
      return undefined;
    }
    return subPeriods.find((sp) => sp.startDate === selectedSubPeriodStart);
  }, [selectedBonusPeriod, selectedSubPeriodStart]);

  // The period whose dates content (boards/dashboards/reports) queries against:
  // the selected sub-period when there is one, otherwise the root period. Fed to
  // PeriodsContext so the existing date-scope funnel picks it up unchanged.
  // Approvals overrides this back to the root period (see ApprovalsScreen).
  const effectivePeriod = useMemo((): Period | undefined => {
    if (selectedSubPeriod) {
      return {
        label: selectedSubPeriod.label,
        startDate: selectedSubPeriod.startDate,
        endDate: selectedSubPeriod.endDate,
      };
    }
    return selectedBonusPeriod;
  }, [selectedSubPeriod, selectedBonusPeriod]);

  return {
    bonusPeriods,
    selectedBonusPeriod,
    setSelectedBonusPeriod: onBonusPeriodSelected,
    selectedSubPeriod,
    setSelectedSubPeriod: onSubPeriodSelected,
    effectivePeriod,
    setSelectedPeriod,
    periods: bonusPeriods,
  };
};

export const BonusDatasetPeriodsProvider = ({
  dataset,
  children,
}: {
  dataset: string;
  children: JSX.Element | JSX.Element[];
}) => {
  const {
    bonusPeriods,
    selectedBonusPeriod,
    setSelectedBonusPeriod,
    selectedSubPeriod,
    setSelectedSubPeriod,
    periods,
    effectivePeriod,
    setSelectedPeriod,
  } = useBonusPeriods({ dataset });

  if (!dataset || !periods || !bonusPeriods || !selectedBonusPeriod) {
    return (
      <>
        <Loading />
      </>
    );
  }

  return (
    <BonusPeriodsContext.Provider
      value={{
        bonusPeriods,
        selectedBonusPeriod,
        setSelectedBonusPeriod,
        selectedSubPeriod,
        setSelectedSubPeriod,
      }}
    >
      <PeriodsContext.Provider
        value={{
          setSelectedPeriod,
          periods,
          selectedPeriod: effectivePeriod,
        }}
      >
        {children}
      </PeriodsContext.Provider>
    </BonusPeriodsContext.Provider>
  );
};

const BonusAppProvider = ({
  app,
  children,
}: {
  app: V3.BonusApp;
  children: JSX.Element | JSX.Element[];
}) => {
  const { bonusPrograms, datasets } = useContext(DatasetDefinitionsContext);
  const [demoOverridenStatusFields, setDemoOverridenStatusFields] = useState<{
    [elasticDocumentId: string]: boolean;
  }>({});
  const program = useMemo(() => {
    return bonusPrograms.find((p) => p.id === app.bonusProgramId);
  }, [bonusPrograms, app.bonusProgramId]);

  const dataset = useMemo(() => {
    if (!program) {
      return undefined;
    }
    return datasets.find((d) => d.type === program.dataset);
  }, [datasets, program]);

  // The active screen's nav item is the last path segment. Approvals keeps the
  // status-priority default period; other screens default a sub-period programme
  // to the current week (see useBonusPeriods).
  const { pathname } = useLocation();
  const isApprovalsScreen = useMemo(() => {
    const itemKey = pathname.split('/').filter(Boolean).pop();
    if (!itemKey) {
      return false;
    }
    let selectedItem: V3.Navigation.Item | undefined;
    app.navigationSettings.sections.forEach((section) => {
      section.items.forEach((item) => {
        if (item.key === itemKey) {
          selectedItem = item;
        }
      });
    });
    return !!selectedItem && appNavItemTypeCheckers.isApprovals(selectedItem);
  }, [app.navigationSettings.sections, pathname]);

  const {
    bonusPeriods,
    selectedBonusPeriod,
    setSelectedBonusPeriod,
    selectedSubPeriod,
    setSelectedSubPeriod,
    periods,
    effectivePeriod,
    setSelectedPeriod,
  } = useBonusPeriods({
    dataset: dataset?.type,
    programId: program?.id,
    isApprovalsScreen,
  });

  if (!dataset || !program || !periods || !bonusPeriods) {
    return (
      <>
        <Loading />
      </>
    );
  }

  if (bonusPeriods.length === 0) {
    return (
      <BonusPeriodsContext.Provider
        value={{
          bonusPeriods: [],
          selectedBonusPeriod: undefined,
          setSelectedBonusPeriod,
          selectedSubPeriod: undefined,
          setSelectedSubPeriod,
        }}
      >
        <PeriodsContext.Provider
          value={{
            setSelectedPeriod,
            periods: [],
            selectedPeriod: undefined,
          }}
        >
          {children}
        </PeriodsContext.Provider>
      </BonusPeriodsContext.Provider>
    );
  }

  if (!selectedBonusPeriod) {
    return (
      <>
        <Loading />
      </>
    );
  }

  return (
    <BonusAppContext.Provider
      value={{
        bonusApp: app,
        dataset,
        bonusProgram: program,
        selectedBonusPeriod,
        demoOverridenStatusFields,
        setDemoOverridenStatusFields,
      }}
    >
      <BonusSubAppBoardSignOffsProvider
        app={app}
        selectedPeriodId={selectedBonusPeriod.id}
      >
        <BonusPeriodsContext.Provider
          value={{
            bonusPeriods,
            selectedBonusPeriod,
            setSelectedBonusPeriod,
            selectedSubPeriod,
            setSelectedSubPeriod,
          }}
        >
          <PeriodsContext.Provider
            value={{
              setSelectedPeriod,
              periods,
              selectedPeriod: effectivePeriod,
            }}
          >
            {children}
          </PeriodsContext.Provider>
        </BonusPeriodsContext.Provider>
      </BonusSubAppBoardSignOffsProvider>
    </BonusAppContext.Provider>
  );
};

export default BonusAppProvider;
